import { useState } from 'react'
import { Eye, EyeOff, AlertCircle, Loader2 } from 'lucide-react'
import { motion } from 'framer-motion'
import { DEFAULT_SETTINGS } from '@/api/portalSettings'
import type { PortalPublicBranding } from '@/hooks/usePortalAuth'

export default function PortalLogin({ branding, error, onLogin }: {
  branding: PortalPublicBranding | null
  error:    string | null
  onLogin:  (username: string, password: string) => Promise<void>
}) {
  const b = branding?.branding ?? DEFAULT_SETTINGS.branding
  const [username, setUsername] = useState('')
  const [password, setPassword] = useState('')
  const [showPw, setShowPw]     = useState(false)
  const [loading, setLoading]   = useState(false)

  const grad = `linear-gradient(135deg, ${b.primaryColor} 0%, ${b.accentColor} 100%)`

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!username.trim() || !password) return
    setLoading(true)
    try { await onLogin(username.trim(), password) } finally { setLoading(false) }
  }

  return (
    <div className="min-h-screen flex items-center justify-center p-6" style={{ background: '#f1f5f9' }}>
      <motion.div initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }}
        className="w-full max-w-sm bg-white rounded-2xl shadow-lg border border-gray-200/60 overflow-hidden">
        <div className="px-8 py-10 text-center" style={{ background: grad }}>
          {b.logoUrl
            ? <img src={b.logoUrl} alt={b.companyName} className="h-10 mx-auto mb-3 object-contain" />
            : <h1 className="text-xl font-extrabold text-white mb-1">{b.companyName}</h1>
          }
          <p className="text-white/70 text-sm">Sign in to your support portal</p>
        </div>

        <form onSubmit={handleSubmit} className="px-8 py-7 space-y-4">
          {error && (
            <div className="flex items-start gap-2.5 px-4 py-3 bg-red-50 border border-red-200 rounded-xl text-sm text-red-600">
              <AlertCircle className="w-4 h-4 flex-shrink-0 mt-0.5" />
              <span>{error}</span>
            </div>
          )}

          <div>
            <label className="block text-xs font-medium text-gray-500 mb-1.5 uppercase tracking-wider">Email</label>
            <input type="text" autoComplete="username" autoFocus value={username}
              onChange={e => setUsername(e.target.value)}
              className="w-full px-4 py-2.5 rounded-xl border border-gray-200 bg-white text-gray-900 text-sm placeholder:text-gray-400 outline-none focus:ring-2 focus:border-transparent transition-all"
              style={{ ['--tw-ring-color' as string]: b.primaryColor + '40' }}
              placeholder="you@company.com" />
          </div>

          <div>
            <label className="block text-xs font-medium text-gray-500 mb-1.5 uppercase tracking-wider">Password</label>
            <div className="relative">
              <input type={showPw ? 'text' : 'password'} autoComplete="current-password" value={password}
                onChange={e => setPassword(e.target.value)}
                className="w-full px-4 py-2.5 pr-10 rounded-xl border border-gray-200 bg-white text-gray-900 text-sm placeholder:text-gray-400 outline-none focus:ring-2 focus:border-transparent transition-all"
                style={{ ['--tw-ring-color' as string]: b.primaryColor + '40' }}
                placeholder="••••••••" />
              <button type="button" tabIndex={-1} onClick={() => setShowPw(v => !v)}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 transition-colors">
                {showPw ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
              </button>
            </div>
          </div>

          <button type="submit" disabled={loading || !username.trim() || !password}
            className="w-full mt-2 py-3 rounded-xl text-sm font-semibold text-white disabled:opacity-40 transition-all hover:shadow-md active:scale-[0.98]"
            style={{ background: b.primaryColor }}>
            {loading ? <span className="flex items-center justify-center gap-2"><Loader2 className="w-4 h-4 animate-spin" />Signing in…</span> : 'Sign in'}
          </button>
        </form>
      </motion.div>
    </div>
  )
}
